import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';
import { pushViewItem } from './data-layer-helper.js';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Array<{id: number, available: boolean, options: string[]}>} */
  #variants = [];

  /** @type {number} */
  #widthOptionPosition = -1;

  /** @type {number} */
  #sizeOptionPosition = -1;

  /** @type {string | null} */
  #selectedWidth = null;

  /** @type {boolean} */
  #maintainSizeOnColorChange = true;

  /** @type {string} */
  static WIDTH_STORAGE_KEY = 'selectedWidth';

  /** @type {boolean} */
  static hasClearedInitialWidthStorage = false;

  connectedCallback() {
    super.connectedCallback();
    if (!VariantPicker.hasClearedInitialWidthStorage) {
      sessionStorage.removeItem(VariantPicker.WIDTH_STORAGE_KEY);
      VariantPicker.hasClearedInitialWidthStorage = true;
    }

    // Read the theme setting
    this.#maintainSizeOnColorChange = this.dataset.maintainSizeOnColorChange === 'true';
    this.#initializeVariantPicker();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('click', this.handleClick.bind(this));

    // Listen for color changes to re-initialize after product switch
    document.addEventListener('color-change', this.#handleColorChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('color-change', this.#handleColorChange);
  }

  /**
   * Initializes or re-initializes the variant picker state.
   * Called on connect and after color changes.
   */
  #initializeVariantPicker() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Reset state
    this.#radios = [];
    this.#checkedIndices = [];
    this.#widthOptionPosition = -1;
    this.#sizeOptionPosition = -1;

    // Load variant data for width-to-size filtering
    this.loadVariantData();

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      // Always push to checkedIndices to maintain array index alignment with fieldsets
      // Use empty array when no option is pre-selected (e.g., "Select a size" state)
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }

      // Identify width and size option positions
      const firstRadio = radios[0];
      if (firstRadio) {
        const fieldsetElement = fieldset;
        
        // Check if this is a width option
        if (fieldsetElement.classList.contains('variant-option--width')) {
          this.#widthOptionPosition = fieldsetIndex;
        }
        
        // Check if this is a size option (not width and not swatch)
        const isNotSwatch = !fieldsetElement.classList.contains('variant-option--swatches');
        const isNotWidth = !fieldsetElement.classList.contains('variant-option--width');
        if (isNotSwatch && isNotWidth) {
          this.#sizeOptionPosition = fieldsetIndex;
        }
      }
    });
    
    // Initialize size options based on selected width
    if (this.#widthOptionPosition >= 0 && this.#sizeOptionPosition >= 0) {
      this.initializeSizeOptions();
    }

    // Restore previously selected width from sessionStorage if product has width options
    if (this.#widthOptionPosition >= 0) {
      const storedWidth = sessionStorage.getItem(VariantPicker.WIDTH_STORAGE_KEY);
      if (storedWidth) {
        const widthRadios = this.#radios[this.#widthOptionPosition];
        const matchingRadio = widthRadios?.find(radio => radio.value === storedWidth);
        if (matchingRadio) {
          matchingRadio.checked = true;
          matchingRadio.dataset.currentChecked = 'true';
          this.#selectedWidth = storedWidth;
          // Update size options for the restored width
          if (this.#sizeOptionPosition >= 0) {
            this.initializeSizeOptions();
          }
        }
      }
    }

    // Update color swatch URLs if size or width is already selected (via URL parameter)
    const hasSelectedSize = this.#sizeOptionPosition >= 0
      ? this.#radios[this.#sizeOptionPosition]?.some(radio => radio.checked)
      : false;
    const hasSelectedWidth = this.#widthOptionPosition >= 0
      ? this.#radios[this.#widthOptionPosition]?.some(radio => radio.checked)
      : false;

    if (hasSelectedSize || hasSelectedWidth) {
      this.updateColorSwatchUrls();
    }
  }

  /**
   * Manually refreshes the Attentive BIS trigger for the current variant.
   * Forces attributes and visibility back if Attentive strips/hides them.
   * @param {Document | HTMLElement} source - The new HTML source containing eligibility JSON
   */
  #refreshAttentiveWaitlist(source) {
    const triggerDiv = document.querySelector('#attn-waitlist-trigger');
    const eligibilityScript = source.querySelector('#attn-waitlist-eligibility');
    
    if (!triggerDiv || !eligibilityScript) return;

    try {
      const eligibilityData = JSON.parse(eligibilityScript.textContent);
      const selectedVariantId = this.selectedOption?.dataset.variantId;

      if (selectedVariantId) {
        // Restore stripped data attributes
        triggerDiv.setAttribute('data-variant-id', selectedVariantId);
        triggerDiv.setAttribute('data-product-id', this.dataset.productId || '');

        const isEligible = eligibilityData[selectedVariantId] === true;

        if (isEligible) {
          // Force visibility over Attentive's inline display:none
          triggerDiv.setAttribute('qty', 'false');
          triggerDiv.style.setProperty('display', 'block');
          
          // Re-trigger SDK to inject the button into the newly attribute-synced div
          if (window.__attentive && typeof window.__attentive.trigger === 'function') {
            window.__attentive.trigger();
          }
        } else {
          triggerDiv.setAttribute('qty', 'true');
          triggerDiv.style.setProperty('display', 'none');
        }
      }
    } catch (e) {
      console.error('Failed to refresh Attentive on variant change:', e);
    }
  }

  /**
   * Handles color change events from color-selector.
   * Re-initializes the variant picker after DOM has been swapped.
   * @param {CustomEvent} event - The color-change event.
   */
  /**
   * @param {CustomEvent} event
   */
  #handleColorChange = (event) => {
    // Re-query refs since DOM was replaced
    const newFieldsets = Array.from(this.querySelectorAll('fieldset[ref="fieldsets[]"]'));
    if (newFieldsets.length > 0) {
      /** @type {any} */ (this.refs).fieldsets = /** @type {HTMLFieldSetElement[]} */ (newFieldsets);
    }
    
    // Update product data attributes
    const newVariantPicker = document.querySelector('variant-picker');
    if (newVariantPicker instanceof HTMLElement) {
      this.dataset.productId = newVariantPicker.dataset.productId;
      this.dataset.productUrl = newVariantPicker.dataset.productUrl;
    }
    
    // Re-initialize variant picker state
    this.#initializeVariantPicker();
    
    // Dispatch VariantUpdateEvent so product-form updates
    const { newVariantId } = event.detail;
    
    if (newVariantId) {
      const variantsScript = this.querySelector('script[data-variants]');
      if (variantsScript?.textContent) {
        try {
          const data = JSON.parse(variantsScript.textContent);
          const variant = data.variants?.find(
            /** @param {{id: number, available: boolean, options: string[]}} v */
            (v) => String(v.id) === String(newVariantId)
          );
          
          if (variant) {
            this.dispatchEvent(
              new VariantUpdateEvent(data, String(variant.id), {
                html: document,
                productId: this.dataset.productId ?? '',
                newProduct: { id: this.dataset.productId ?? '', url: this.dataset.productUrl ?? '' }
              })
            );

            // Sync Attentive Waitlist after color change
            this.#refreshAttentiveWaitlist(document);

            const isOnProductPage =
              this.dataset.templateProductMatch === 'true' &&
              !this.closest('product-card') &&
              !this.closest('quick-add-dialog');

            if (isOnProductPage && data.product) {
              pushViewItem(data.product, variant);
            }
          }
        } catch (e) {
          console.error('Failed to parse variant data for color change:', e);
        }
      }
    }
  };

  /**
   * Handles click events for variant options.
   * Note: Out-of-stock variants are now selectable to enable "Notify Me" functionality.
   * Inputs are never disabled in the Liquid template - only aria-disabled is used.
   * @param {Event} event - The click event.
   */
  handleClick(event) {
    // No-op: All variant options are clickable for "Notify Me" functionality.
    // Unavailable options use data-option-available="false" and aria-disabled="true"
    // for styling and accessibility, but are not actually disabled.
  }

  /**
   * Loads variant data from the JSON script tag.
   */
  loadVariantData() {
    try {
      const variantsScript = this.querySelector('script[data-variants]');
      if (variantsScript && variantsScript.textContent) {
        const data = JSON.parse(variantsScript.textContent);
        this.#variants = data.variants || [];
      }
    } catch (error) {
      console.error('Failed to parse variant data:', error);
      this.#variants = [];
    }
  }

  /**
   * Initializes size options based on the currently selected width.
   */
  initializeSizeOptions() {
    if (this.#widthOptionPosition < 0 || this.#sizeOptionPosition < 0) return;
    
    const widthRadios = this.#radios[this.#widthOptionPosition];
    const selectedWidthRadio = widthRadios?.find(radio => radio.checked);
    
    if (selectedWidthRadio) {
      const selectedWidth = selectedWidthRadio.value;
      this.updateSizeOptionsForWidth(selectedWidth);
    }
  }

  /**
   * Updates size option availability based on selected width.
   * @param {string} selectedWidth - The selected width value.
   */
  updateSizeOptionsForWidth(selectedWidth) {
    if (this.#sizeOptionPosition < 0 || this.#variants.length === 0) return;

    const sizeRadios = this.#radios[this.#sizeOptionPosition];
    if (!sizeRadios) return;

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const sizeFieldset = fieldsets[this.#sizeOptionPosition];
    if (!sizeFieldset) return;


    // Update each size option
    sizeRadios.forEach(sizeRadio => {
      const sizeValue = sizeRadio.value;
      const label = sizeRadio.closest('label');
      if (!label) return;

      // Find if there's an available variant for this width + size combination
      let hasAvailableVariant = false;
      let matchingVariantId = null;

      for (const variant of this.#variants) {
        const variantWidth = this.#widthOptionPosition >= 0 ? variant.options[this.#widthOptionPosition] : null;
        const variantSize = variant.options[this.#sizeOptionPosition];

        // Check if this variant matches the selected width and current size
        if (variantSize === sizeValue && variantWidth === selectedWidth) {
          matchingVariantId = variant.id;
          if (variant.available) {
            hasAvailableVariant = true;
            break;
          }
        }
      }

      // Update the radio button's data attributes
      if (matchingVariantId) {
        sizeRadio.dataset.variantId = String(matchingVariantId);
        sizeRadio.dataset.optionAvailable = String(hasAvailableVariant);

        if (hasAvailableVariant) {
          // Enable the input - it's available for purchase
          sizeRadio.disabled = false;
          sizeRadio.removeAttribute('aria-disabled');
          
          // Hide the strikethrough SVG if it exists
          const svg = label.querySelector('svg');
          if (svg) {
            svg.style.display = 'none';
          }
        } else {
          // IMPORTANT: Do NOT disable the input - users need to click unavailable 
          // options to trigger "Notify Me" functionality. Use data-option-available
          // for styling instead.
          sizeRadio.disabled = false;
          sizeRadio.setAttribute('aria-disabled', 'true');
          
          // Show or create the strikethrough SVG
          let svg = label.querySelector('svg');
          if (!svg) {
            // Create the strikethrough SVG if it doesn't exist
            svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('viewBox', '0 0 100 46');
            svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
            
            // Create the first line
            const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line1.setAttribute('x1', '100');
            line1.setAttribute('y1', '0');
            line1.setAttribute('x2', '0');
            line1.setAttribute('y2', '46');
            line1.setAttribute('vector-effect', 'non-scaling-stroke');
            
            // Create the duplicate line for motion overlay
            const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line2.setAttribute('x1', '100');
            line2.setAttribute('y1', '0');
            line2.setAttribute('x2', '0');
            line2.setAttribute('y2', '46');
            line2.setAttribute('vector-effect', 'non-scaling-stroke');
            
            svg.appendChild(line1);
            svg.appendChild(line2);
            label.appendChild(svg);
          }
          svg.style.display = '';
        }
      }
    });

    // Keep the current size selected even if it becomes unavailable for the new width.
    // This allows users to see "Notify Me" for unavailable size/width combinations.
    // The selection will be preserved through the DOM replacement via captureSelectedOptions/applySelectedOptions.
  }

  /**
   * Updates color swatch URLs to include the variant ID for the selected size/width.
   * This ensures that when a user clicks a color swatch, they navigate to the same size/width.
   */
  updateColorSwatchUrls() {
    // Check if the feature is enabled
    if (!this.#maintainSizeOnColorChange) return;

    // Find the color-selector element
    const colorSelector = document.querySelector('color-selector');
    if (!colorSelector) return;

    // Get the preloaded variant data from color-selector
    const variantsScript = colorSelector.querySelector('script[data-all-products-variants]');
    if (!variantsScript?.textContent) return;

    let allProductsVariants;
    try {
      allProductsVariants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      console.error('Failed to parse all-products-variants data:', e);
      return;
    }

    // Get the currently selected size and width
    const selectedSize = this.#sizeOptionPosition >= 0
      ? this.#radios[this.#sizeOptionPosition]?.find(radio => radio.checked)?.value
      : null;
    const selectedWidth = this.#widthOptionPosition >= 0
      ? this.#radios[this.#widthOptionPosition]?.find(radio => radio.checked)?.value
      : null;

    // Also check for stored width from sessionStorage for persistence across color changes
    const storedWidth = sessionStorage.getItem(VariantPicker.WIDTH_STORAGE_KEY);

    // Use stored width if current product doesn't have width options but user previously selected one
    const effectiveWidth = selectedWidth || storedWidth;

    // Require at least size or width to be selected
    if (!selectedSize && !effectiveWidth) return;

    // Update each color swatch's href
    const swatches = colorSelector.querySelectorAll('.color-selector__swatch');
    swatches.forEach(swatch => {
      if (!(swatch instanceof HTMLElement)) return;

      const productHandle = swatch.dataset.productHandle;
      if (!productHandle || !allProductsVariants[productHandle]) return;

      const productData = allProductsVariants[productHandle];
      const productUrl = productData.url;

      // Find the variant ID that matches the selected size/width
      let variantId = null;

      // Get the option positions from the current product's variant data
      // The color-selector preloaded data doesn't have position info, so we need to infer it
      // from the current product's options structure
      const currentVariantsScript = this.querySelector('script[data-variants]');
      let currentVariants = [];
      if (currentVariantsScript?.textContent) {
        try {
          const data = JSON.parse(currentVariantsScript.textContent);
          currentVariants = data.variants || [];
        } catch (e) {
          console.error('Failed to parse current variants:', e);
        }
      }

      // Determine option positions from current product (same as target product structure)
      // We need to find which index in the options array corresponds to size and width
      let sizePosition = -1;
      let widthPosition = -1;

      if (currentVariants.length > 0 && currentVariants[0].options) {
        // Check first variant to determine option order
        const firstVariant = currentVariants[0];
        // We need to match the selected values to their positions
        for (let i = 0; i < firstVariant.options.length; i++) {
          if (selectedSize && firstVariant.options[i] === selectedSize) sizePosition = i;
          if (selectedWidth && firstVariant.options[i] === selectedWidth) widthPosition = i;
        }
      }

      if (sizePosition === -1 && this.#sizeOptionPosition >= 0) sizePosition = this.#sizeOptionPosition;
      if (widthPosition === -1 && this.#widthOptionPosition >= 0) widthPosition = this.#widthOptionPosition;

      if (selectedSize && effectiveWidth) {
        for (const variant of productData.variants) {
          const sizeMatches = sizePosition >= 0 && variant.options[sizePosition] === selectedSize;
          const widthMatches = widthPosition >= 0 && variant.options[widthPosition] === effectiveWidth;

          if (productData.hasWidthOption && widthPosition >= 0) {
            if (sizeMatches && widthMatches) { variantId = variant.id; break; }
          } else {
            if (sizeMatches) { variantId = variant.id; break; }
          }
        }
      }
      else if (selectedSize) {
        for (const variant of productData.variants) {
          if (sizePosition >= 0 && variant.options[sizePosition] === selectedSize) { variantId = variant.id; break; }
        }
      }
      else if (effectiveWidth) {
        for (const variant of productData.variants) {
          if (productData.hasWidthOption && widthPosition >= 0 && variant.options[widthPosition] === effectiveWidth) { variantId = variant.id; break; }
        }
      }

      // Update the href with the variant parameter
      if (variantId) {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('variant', String(variantId));
        swatch.setAttribute('href', url.toString());
      } else {
        // No matching variant found, use the base product URL
        swatch.setAttribute('href', productUrl);
      }
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // If this is a width option change, update size options and color swatch URLs
    if (event.target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(event.target.dataset.fieldsetIndex || '');
      if (fieldsetIndex === this.#widthOptionPosition) {
        const selectedWidth = event.target.value;
        this.#selectedWidth = selectedWidth;
        // Store the selected width in sessionStorage for persistence across color changes
        sessionStorage.setItem(VariantPicker.WIDTH_STORAGE_KEY, selectedWidth);
        this.updateSizeOptionsForWidth(selectedWidth);
        this.updateColorSwatchUrls();
      }
    }

    // If this is a size option change, update color swatch URLs
    if (event.target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(event.target.dataset.fieldsetIndex || '');
      if (fieldsetIndex === this.#sizeOptionPosition) {
        this.updateColorSwatchUrls();
      }
    }

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
    }

    if (loadsNewProduct) url.pathname = newUrl;

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;
          if (currentIndex !== undefined && radios[currentIndex]) radios[currentIndex].dataset.previousChecked = 'false';
          if (previousIndex !== undefined && radios[previousIndex]) radios[previousIndex].dataset.previousChecked = 'false';

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty('--pill-width-current', `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`);
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty('--pill-width-previous', `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`);
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');
      for (const option of target.options) option.removeAttribute('selected');
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      else params.push(`option_values=${selectedOption.dataset.optionValueId}`);
    }

    const variantId = selectedOption.dataset.variantId;
    if (variantId) params.push(`variant=${variantId}`);

    if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, use section-rendering-product-card
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    
    // For product pages, use the section_id from the data attribute for proper Section Rendering API response
    const sectionId = this.dataset.sectionId;
    if (sectionId) params.push(`section_id=${sectionId}`);
    
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * @typedef {Object} SavedSelections
   * @property {string} [width] - The selected width value
   * @property {string} [size] - The selected size value
   * @property {Map<number, string>} optionsByFieldset - Map of fieldset index to selected value
   */

  /**
   * Captures the currently selected options before DOM replacement.
   * @returns {SavedSelections} The saved selections
   */
  captureSelectedOptions() {
    /** @type {Map<number, string>} */
    const optionsByFieldset = new Map();
    
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    checkedInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        const fieldsetIndex = Number.parseInt(input.dataset.fieldsetIndex || '0');
        optionsByFieldset.set(fieldsetIndex, input.value);
      }
    });

    return {
      width: this.#selectedWidth ?? undefined,
      size: this.#sizeOptionPosition >= 0 ? optionsByFieldset.get(this.#sizeOptionPosition) ?? undefined : undefined,
      optionsByFieldset
    };
  }

  /**
   * Applies saved selections to the new variant picker after DOM replacement.
   * @param {HTMLElement} container - The product details container
   * @param {SavedSelections} savedSelections - The saved selections
   */
  applySelectedOptions(container, savedSelections) {
    const newVariantPicker = container.querySelector('variant-picker');
    if (!newVariantPicker) return;

    // Re-select each option by fieldset
    savedSelections.optionsByFieldset.forEach((value, fieldsetIndex) => {
      const fieldsets = newVariantPicker.querySelectorAll('fieldset');
      const fieldset = fieldsets[fieldsetIndex];
      if (!fieldset) return;

      // Find the input with matching value and check it
      const inputs = fieldset.querySelectorAll('input[type="radio"]');
      inputs.forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = input.value === value;
          input.dataset.currentChecked = String(input.value === value);
        }
      });
    });
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Capture selected options BEFORE fetch - Shopify returns first available variant,
    // but we want to preserve user's selection including unavailable options
    const savedSelections = this.captureSelectedOptions();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html, savedSelections);

          // CRITICAL: Refresh Attentive for the new variant selection within the same product
          requestAnimationFrame(() => {
            this.#refreshAttentiveWaitlist(html);
          });

          if (this.selectedOptionId) {
            const parsedData = JSON.parse(textContent);
            this.dispatchEvent(
              new VariantUpdateEvent(parsedData, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );

            // Push view_item event for analytics when variant changes
            // Only fires on product pages (not quick-add dialogs or product cards)
            const isOnProductPage =
              this.dataset.templateProductMatch === 'true' &&
              !this.closest('product-card') &&
              !this.closest('quick-add-dialog');

            if (isOnProductPage) {
              // Parse data-variants JSON (separate from data-selected-variant)
              const variantsScript = html.querySelector('variant-picker script[data-variants]');
              const variantsData = variantsScript?.textContent ? JSON.parse(variantsScript.textContent) : null;

              if (variantsData?.product && variantsData?.variants) {
                // Get variant ID from the hidden input in the response HTML
                // This is more reliable than checking radio inputs for multi-option products
                const variantInput = html.querySelector('input[name="id"][ref="variantId"]');
                const selectedVariantId = variantInput?.getAttribute('value');

                if (selectedVariantId) {
                  const selectedVariant = variantsData.variants.find(
                    /** @param {{ id: number }} v */
                    (v) => String(v.id) === selectedVariantId
                  );

                  if (selectedVariant) {
                    pushViewItem(variantsData.product, selectedVariant);
                  }
                }
              }
            }
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker and product details.
   * @param {Document} newHtml - The new HTML.
   * @param {SavedSelections} [savedSelections] - Previously captured selections to restore.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml, savedSelections) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Update the mobile PDP header (price, title, reviews shown on mobile above the media)
    // This is separate from product-details and needs to be updated independently
    const topHeader = document.querySelector('[data-product-top-header]');
    const newTopHeader = newHtml.querySelector('[data-product-top-header]');
    
    if (topHeader && newTopHeader) {
      topHeader.innerHTML = newTopHeader.innerHTML;
    }

    // Update only the variant picker itself - NOT the buy-buttons/product-form
    // The product-form-component handles its own updates via VariantUpdateEvent
    // This avoids unnecessary repainting of buy-buttons on variant change
    morph(this, newVariantPickerSource);
    
    // Update price blocks separately since they're outside the variant picker
    // The product-price component's event listener might fail due to productId checks
    const currentPrices = document.querySelectorAll('product-price');
    currentPrices.forEach(currentPrice => {
      if (!(currentPrice instanceof HTMLElement)) return;
      const currentProductId = currentPrice.dataset.productId;
      // Find matching price in new HTML by product ID
      const newPrices = newHtml.querySelectorAll('product-price');
      newPrices.forEach(newPrice => {
        if (!(newPrice instanceof HTMLElement)) return;
        if (newPrice.dataset.productId === currentProductId) {
          const currentPriceContainer = currentPrice.querySelector('[ref="priceContainer"]');
          const newPriceContainer = newPrice.querySelector('[ref="priceContainer"]');
          if (currentPriceContainer && newPriceContainer && currentPriceContainer.innerHTML !== newPriceContainer.innerHTML) {
            currentPriceContainer.replaceWith(newPriceContainer);
          }
        }
      });
    });
    
    // After morph, we need to reload variant data and re-apply size filtering
    this.loadVariantData();
    
    // Re-initialize the radios array after morph
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    this.#radios = [];
    this.#checkedIndices = [];
    
    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });
    
    // Re-identify width and size option positions after morph
    fieldsets.forEach((fieldset, fieldsetIndex) => {
      if (fieldset.classList.contains('variant-option--width')) {
        this.#widthOptionPosition = fieldsetIndex;
      }
      const isNotSwatch = !fieldset.classList.contains('variant-option--swatches');
      const isNotWidth = !fieldset.classList.contains('variant-option--width');
      if (isNotSwatch && isNotWidth) {
        this.#sizeOptionPosition = fieldsetIndex;
      }
    });
    
    // Restore user's selections after morph
    if (savedSelections) {
      savedSelections.optionsByFieldset.forEach((value, fieldsetIndex) => {
        const fieldset = fieldsets[fieldsetIndex];
        if (!fieldset) return;
        
        const inputs = fieldset.querySelectorAll('input[type="radio"]');
        inputs.forEach((input) => {
          if (input instanceof HTMLInputElement) {
            if (input.value === value) {
              input.checked = true;
              input.dataset.currentChecked = 'true';
            } else {
              input.checked = false;
              input.dataset.currentChecked = 'false';
            }
          }
        });
      });
    }
    
    // If we have width and size options, re-apply the size filtering
    // This is critical to update strikethrough states after variant changes
    if (this.#widthOptionPosition >= 0 && this.#sizeOptionPosition >= 0) {
      // Get the currently selected width (either from saved selections or from the DOM)
      const savedWidth = savedSelections?.width;
      const widthRadios = this.#radios[this.#widthOptionPosition];
      const selectedWidthRadio = widthRadios?.find(radio => radio.checked);
      const currentWidth = savedWidth || selectedWidthRadio?.value;
      
      if (currentWidth) {
        this.#selectedWidth = currentWidth;
        this.updateSizeOptionsForWidth(currentWidth);
      }
    }

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}